<template>
    <div class="content-type-collection fill-height mb-16 position-relative">
        <ContainerContent
            outer-container
        >
            <v-row>
                <v-col>
                    <CollectionsHeader
                        v-if="contentMeta"
                        :content-meta="contentMeta"
                    ></CollectionsHeader>
                </v-col>
            </v-row>

            <!-- Main -->
            <v-row>

                <!-- Tool Bar -->
                <v-col
                    cols="12"
                >
                    <v-toolbar
                        tag="div"
                        density="compact"
                        rounded="t"
                    >
                        <!-- Summary -->
                        <div
                            class="ms-4"
                            :class="{ 'font-size-8': isDisplayXs }"
                        >
                            <span class="font-weight-bold">{{ totalCount }}</span>
                            product<template v-if="totalCount > 1">s</template>
                        </div>

                        <v-spacer></v-spacer>

                        <!-- Show/Hide Filters -->
                        <v-btn
                            v-if="isMobile"
                            color="secondary"
                            @click="toggleFilters()"
                            icon
                        >
                            <v-icon :icon="mdiTuneVariant"></v-icon>
                        </v-btn>

                        <!-- Sort -->
                        <CollectionsSortInput
                            v-model="queryParamsObj.ordering"
                            :compact="isMobile"
                            @on-change="onFilterUpdated"
                        ></CollectionsSortInput>
                    </v-toolbar>
                    <div
                        class="filters-top-bar"
                    >
                        <CollectionsFilterTags
                            v-if="filtersModel && (filters && !lo_isError(filters)) && !!filtersAppliedCount"
                            class="px-4"
                            v-model="filtersModel"
                            :filters="filters"
                            :products-loading="isProductsLoading"
                            :products-paging="isProductsPaging"
                            @on-reset-filters="onFilterClear"
                            @on-change="onFilterTagChange"
                        ></CollectionsFilterTags>
                    </div>
                </v-col>

                <!-- Products & Filters -->
                <v-col
                    cols="12"
                    class="pt-0"
                >
                    <div
                        class="products-container"
                        :class="{ 'filters-shown': !isMobile }"
                    >
                        <!-- Filter Column (CSS Grid) -->
                        <div
                            class="filters-container"
                        >
                            <div v-if="!isMobile">
                                <ProductsFilter
                                    :filters="filters"
                                    :filters-model="filtersModel"
                                    :products-loading="isProductsLoading"
                                    :products-paging="isProductsPaging"
                                    :resource-path="contentPath"
                                    @on-update="onFilterUpdated()"
                                ></ProductsFilter>
                            </div>
                        </div>

                        <!-- Products Column (CSS Grid) -->
                        <div
                            class="product-list"
                        >
                            <ProductsList
                                :products="products"
                                :page-key="pageKey"
                                :products-loading="isProductsLoading"
                                :products-paging="isProductsPaging"
                                :has-next-link="hasNextLink"
                                :has-prev-link="hasPrevLink"
                                :filters-applied-count="filtersAppliedCount"
                                @on-get-prev="onProductPrev()"
                                @on-get-next="onProductNext()"
                            ></ProductsList>
                        </div>
                    </div>
                </v-col>
            </v-row>
        </ContainerContent>

        <!-- Scroll Top Button (FAB) -->
        <ScrollTopBtn></ScrollTopBtn>

        <!-- Mobile Filters Modal -->
        <ResponsiveModal v-model="filterDialogShown">
            <template #default="{ onExit, openCount }">
                <v-card
                    class="dialog-filters fill-height in-modal"
                >
                    <v-card-title
                        class="d-block pa-0"
                    >
                        <v-toolbar
                            color="secondary"
                            density="compact"
                        >
                            <v-toolbar-title
                                class="text-h6"
                            >
                                Filters
                            </v-toolbar-title>
                            <v-btn
                                @click="onExit"
                                :icon="mdiWindowClose"
                            ></v-btn>
                        </v-toolbar>
                    </v-card-title>
                    <v-card-text
                        class="mt-0 px-4 pb-10"
                    >
                        <ProductsFilter
                            :filters="filters"
                            :filters-model="filtersModel"
                            :products-loading="isProductsLoading"
                            :products-paging="isProductsPaging"
                            :resource-path="contentPath"
                            @on-update="onFilterUpdated()"
                        ></ProductsFilter>
                    </v-card-text>
                    <v-card-actions>
                        <v-row dense>
                            <v-col
                                cols="6"
                                sm="3"
                            >
                                <v-btn
                                    type="button"
                                    variant="flat"
                                    class="mr-4"
                                    color="accent"
                                    size="large"
                                    @click="onFilterClear"
                                    block
                                >
                                    Clear All
                                </v-btn>
                            </v-col>
                            <v-col
                                cols="6"
                                sm="9"
                            >
                                <v-btn
                                    type="button"
                                    variant="flat"
                                    class="mr-4"
                                    color="secondary"
                                    size="large"
                                    @click="onExit"
                                    block
                                >
                                    Apply
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-card-actions>
                </v-card>
            </template>
        </ResponsiveModal>
    </div>
</template>

<script lang="ts" setup>
    import type { NuxtError } from '#app';
    import {
        useIsMobile,
        useProductCollection,
        useGlobalAlert,
        useLogging,
        useCustomNuxtUtils
    } from '~/composables';
    import ResponsiveModal from 'x1-base/components/ResponsiveModal/ResponsiveModal.vue';
    import ContainerContent from '~/components/ContainerContent/ContainerContent.vue';
    import CollectionsHeader from '~/components/content/collections/CollectionsHeader/CollectionsHeader.vue';
    import ProductsFilter from '~/components/products/products-list/ProductsFilter/ProductsFilter.vue';
    import ProductsList from '~/components/products/products-list/ProductsList/ProductsList.vue';
    import type {
        CustomPagination,
        Product,
        ContentMeta,
        ProductFilterAttribute
    } from '~/types';
    import { throttle as lo_throttle } from 'es-toolkit';
    import { isError as lo_isError } from 'es-toolkit';
    import { trim as lo_trim } from 'es-toolkit';
    import { mdiTuneVariant, mdiWindowClose } from '@mdi/js';

    const ScrollTopBtn = defineAsyncComponent(() => import('~/components/ScrollTopBtn/ScrollTopBtn.vue'));
    const CollectionsSortInput = defineAsyncComponent(() => import('~/components/content/collections/CollectionsSortInput/CollectionsSortInput.vue'));
    const CollectionsFilterTags = defineAsyncComponent(() => import('~/components/content/collections/CollectionsFilterTags/CollectionsFilterTags.vue'));


    const props = defineProps<{
        contentData: CustomPagination<Product>;
        contentMeta: ContentMeta;
        pageKey: string;
    }>();

    const route = useRoute();
    const {
        removeOnceKeysByPrefix,
        removeDataKey,
        isPostServerRendered
    } = useCustomNuxtUtils();
    const contentPath = computed<string>(() => lo_trim(route.path, '/'));
    const products = ref<Product[]>([]);
    const createGlobalAlert = useGlobalAlert();

    const useProductCollectionObj = useProductCollection();

    if (isPostServerRendered.value) {
        useProductCollectionObj.resetState();
    }

    useProductCollectionObj.init(
        props.contentMeta.uid!,
        contentPath.value,
        route.query
    );

    const {
        filtersModel,
        filtersAppliedCount,
        queryParamsObj,
        totalCount,
        hasNextLink,
        hasPrevLink
    } = useProductCollectionObj;

    const isProductsLoading = ref(true);
    const isProductsPaging = ref(false);
    const { isMobile, isDisplayXs } = useIsMobile();
    const { logSentryError } = useLogging();
    const filterDialogShown = ref(false);

    const unWatchIsMobile = watch(
        isMobile,
        (newIsMobile): void => {
            if (!newIsMobile) {
                filterDialogShown.value = false;
            }
        }
    );

    const keyFilters = `keyProdCollFilters_${props.contentMeta.uid ?? 'no_uid'}`;
    const { data: filters } = useLazyAsyncData<ProductFilterAttribute[] | Error>(
        keyFilters,
        () => useProductCollectionObj.getFilters()
    );

    function toggleFilters(): void {
        filterDialogShown.value = !filterDialogShown.value;
    }

    const onProductNext = lo_throttle(async (): Promise<void> => await handleLoad(true), 1200, { edges: ['leading'] });
    const onProductPrev = lo_throttle(async (): Promise<void> => await handleLoad(false), 1200, { edges: ['leading'] });

    async function handleLoad(isNext = true): Promise<void> {
        const action = isNext ? 'getNext' : 'getPrev';
        const method = isNext ? 'push' : 'unshift';

        isProductsPaging.value = true;
        const response = await useProductCollectionObj[action]();
        isProductsPaging.value = false;

        if (lo_isError(response)) {
            onError('col-002');
            return;
        }

        if (!Array.isArray(response.data)) {
            onError('col-003');
            return;
        }

        products.value[method](...response.data);
    }

    function onFilterTagChange([pUid, uid, val]: [parentUid: string, uid: string, value: boolean]): void {
        if (filtersModel.value) {
            filtersModel.value[pUid]![uid] = val;
            onFilterUpdated();
        }
    }

    async function onFilterUpdated(): Promise<void> {
        removeOnceKeysByPrefix(props.pageKey);

        if (filtersModel.value) {
            isProductsLoading.value = true;

            const filters = useProductCollectionObj.processFiltersModel();
            const query = useProductCollectionObj.getQueryObj({ f: filters });
            await navigateTo({ path: route.path, query });

            const response = await useProductCollectionObj.getCollection(query);

            // Clear current items
            await nextTick();
            products.value.splice(0);
            await nextTick();


            // Success
            if (!lo_isError(response)) {
                if (response.data?.length) {
                    products.value.push(...response.data);
                }

                await nextTick();
                isProductsLoading.value = false;
                processTracking();
                return;
            }

            // Error
            isProductsLoading.value = false;
            onError('col-001', response);
        } else {
            console.warn('onFilterUpdated: No Filters Model');
        }
    }

    async function onFilterClear(): Promise<void> {
        useProductCollectionObj.resetFilters();
        await onFilterUpdated();
    }

    function processTracking(): void {
        // TODO: v3: ga4: Needs params
        // REF: https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#view_item_list
        // useTrackEvent('view_item_list', {
        // });
    }

    function onError(errCode?: string, errObj?: Error | NuxtError): void {
        let msg = 'Error loading products. Please try again.';

        if (errCode) {
            msg += ` Reference ID: ${errCode}`;
        }

        filterDialogShown.value = false;
        isProductsLoading.value = false;
        isProductsPaging.value = false;

        logSentryError(
            errObj ?? new Error('Generic Type Collections Error'),
            {
                tags: {
                    section: 'type-collections'
                },
                extra: {
                    event_id: errCode ?? '0'
                }
            }
        );

        createGlobalAlert({
            text: msg,
            show: true,
            type: 'error',
            timeout: 5000
        });
    }

    onBeforeMount(() => {
        if (props.contentData) {
            isProductsLoading.value = false;
            const preLoadedProds = useProductCollectionObj.processContentResponse(props.contentData);
            products.value.push(...preLoadedProds.data);
            isProductsLoading.value = false;
        }
    });

    onBeforeUnmount(() => {
        removeDataKey(keyFilters);
        unWatchIsMobile();
    });
</script>

<style lang="scss">
.content-type-collection {
    $side-panel-width: 260px;
    $panel-transition: all 0.1s ease;

    .filters-top-bar {
        height: 42px;
    }

    .products-container {
        width: 100%;
        max-width: 100%;
        display: grid;
        grid-template-columns: 0 minmax(0, 1fr);

        grid-template-rows: 1fr;
        grid-column-gap: 0;
        grid-row-gap: 0px;
        transition: $panel-transition;

        .filters-container > div {
            display: none;
        }

        &.filters-shown {
            grid-template-columns: $side-panel-width minmax(0, 1fr);
            grid-column-gap: 1rem;
            transition: $panel-transition;

            .filters-container > div {
                display: unset;
            }
        }
    }

    .filters-container {
        max-width: $side-panel-width;
        grid-area: 1 / 1 / 2 / 2;
    }
    .product-list {
        grid-area: 1 / 2 / 2 / 3;
        transition: $panel-transition;
    }
}
</style>
